import React, { useState, useCallback } from 'react';
import { CloseCircleOutlined, LeftOutlined, RightOutlined } from '@ant-design/icons';
import { notification, Spin } from 'antd';
import { DISAGREE_MESSAGES, DISAGREE_MIN_CHARACTERS, DISAGREE_MAX_CHARACTERS } from '../../utils/constants';
import { submitDisagreeAiFeedback, EvaluationResult } from '../../../../../../redux/api/questionsApi';
import {
  Overlay,
  ModalContainer,
  ModalIcon,
  ModalTitle,
  ModalSubtitle,
  ModalTextArea,
  TextAreaWrapper,
  CharacterCount,
  ModalActions,
  ModalButtonBack,
  ModalButtonSubmit,
} from './styles';

interface DisagreeModalProps {
  questionId: number;
  provaId?: number;
  studentAnswer?: string;
  evaluationResult?: EvaluationResult | null;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const DisagreeModal: React.FC<DisagreeModalProps> = ({
  questionId,
  provaId,
  studentAnswer,
  evaluationResult,
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [argument, setArgument] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleOverlayClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (e.target === e.currentTarget) {
        onClose();
      }
    },
    [onClose],
  );

  const handleSubmit = useCallback(async () => {
    if (argument.trim().length < DISAGREE_MIN_CHARACTERS) {
      notification.warning({
        message: DISAGREE_MESSAGES.MIN_LENGTH_ERROR,
        placement: 'topRight',
        duration: 3,
      });
      return;
    }

    setIsSubmitting(true);

    const response = await submitDisagreeAiFeedback({
      questionId,
      provaId,
      disagreementReason: argument.trim(),
      studentAnswer,
      evaluationResult: evaluationResult ?? undefined,
    });

    if (response.success) {
      setArgument('');
      onSuccess();
      onClose();
      requestAnimationFrame(() => {
        notification.success({
          message: DISAGREE_MESSAGES.SUCCESS,
          placement: 'topRight',
          duration: 4,
        });
      });
    } else if (response.alreadyExists) {
      onSuccess();
      onClose();
      requestAnimationFrame(() => {
        notification.info({
          message: response.message,
          placement: 'topRight',
          duration: 5,
        });
      });
    } else {
      notification.error({
        message: response.message || DISAGREE_MESSAGES.ERROR,
        placement: 'topRight',
        duration: 4,
      });
    }

    setIsSubmitting(false);
  }, [argument, questionId, provaId, studentAnswer, evaluationResult, onSuccess, onClose]);

  if (!isOpen) return null;

  return (
    <Overlay onClick={handleOverlayClick}>
      <ModalContainer>
        <ModalIcon>
          <CloseCircleOutlined />
        </ModalIcon>
        <ModalTitle>{DISAGREE_MESSAGES.MODAL_TITLE}</ModalTitle>
        <ModalSubtitle>{DISAGREE_MESSAGES.MODAL_SUBTITLE}</ModalSubtitle>
        <TextAreaWrapper>
          <ModalTextArea
            value={argument}
            onChange={(e) => setArgument(e.target.value)}
            placeholder={DISAGREE_MESSAGES.PLACEHOLDER}
            maxLength={DISAGREE_MAX_CHARACTERS}
          />
          <CharacterCount isNearLimit={argument.length >= DISAGREE_MAX_CHARACTERS * 0.9}>
            {argument.length}/{DISAGREE_MAX_CHARACTERS}
          </CharacterCount>
        </TextAreaWrapper>
        <ModalActions>
          <ModalButtonBack type="button" onClick={onClose}>
            <LeftOutlined />
            {DISAGREE_MESSAGES.BACK_BUTTON}
          </ModalButtonBack>
          <ModalButtonSubmit
            type="button"
            onClick={handleSubmit}
            disabled={isSubmitting || argument.trim().length < DISAGREE_MIN_CHARACTERS}
          >
            {isSubmitting && <Spin size="small" />}
            {isSubmitting ? DISAGREE_MESSAGES.SUBMITTING : DISAGREE_MESSAGES.SUBMIT_BUTTON}
            {!isSubmitting && <RightOutlined />}
          </ModalButtonSubmit>
        </ModalActions>
      </ModalContainer>
    </Overlay>
  );
};
